import { useContext, useState, useEffect } from "react";
import toast from "react-hot-toast";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useParams } from "react-router-dom";
import { programService } from "../../../services/program.service";
import { BrandContext } from "../../../contexts/BrandContext/context";
import type { ProgramSettings as ProgramSettingsType } from "../../../services/program.service";
import { AxiosError } from "axios";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { ChevronDown, Info, Settings, X } from "react-feather";
import { Button } from "@/components/ui/button";
import {
  DialogRoot,
  DialogContent,
  DialogHeader,
  DialogFooter,
} from "@/components/ui/dialog";
import { usePermissions } from "@/contexts/PermissionsContext";
import { PlanFeature } from "@/types/permissions";
import { AffiliateManagementCard } from "@/components/brand/programs/AffiliateManagementCard";
import { JoinContentCard } from "@/components/brand/programs/JoinContentCard";
import { ProgramTiersCard } from "@/components/brand/programs/ProgramTiersCard";
import { RenewalCommissionCard } from "@/components/brand/programs/RenewalCommissionCard";
import { ShareLinkField } from "@/components/brand/programs/ShareLinkField";

const WELCOME_MODAL_ASSETS = {
  screenshot:
    "https://gowgqecldmyodqwyyipg.supabase.co/storage/v1/object/public/arena-images/static-assets/welcome-modal-sample.png",
  demoVideo:
    "https://gowgqecldmyodqwyyipg.supabase.co/storage/v1/object/public/arena-images/static-assets/welcome-modal-demo-video.mp4",
} as const;

function ProgramEditSkeleton() {
  return (
    <div className="animate-pulse space-y-5">
      <div className="rounded-xl border border-border bg-card p-5 space-y-4">
        <div className="h-8 w-64 bg-muted rounded" />
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
          {Array.from({ length: 6 }).map((_, index) => (
            <div key={index} className="space-y-2">
              <div className="h-3 w-24 bg-muted rounded" />
              <div className="h-10 bg-muted rounded-lg w-full" />
            </div>
          ))}
        </div>
      </div>
      <div className="rounded-xl border border-border bg-card h-80" />
    </div>
  );
}

export default function ProgramEdit() {
  const { fetchBrandDetails, brandDetails } = useContext(BrandContext);
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { programId } = useParams<{ programId: string }>();
  const { hasFeature } = usePermissions();
  const hasProgramNamingFeature = hasFeature(PlanFeature.PROGRAM_NAMING);
  const [settingsOpen, setSettingsOpen] = useState(false);

  // Fetch the specific program by ID
  const {
    data: program,
    isLoading: isLoadingPrograms,
    isFetching: isFetchingPrograms,
    error: programError,
  } = useQuery({
    queryKey: ["program", programId],
    queryFn: () => programService.getProgramById(programId!),
    enabled: !!programId,
    retry: (failureCount, error) => {
      // Don't retry on 404 (program not found)
      if ((error as AxiosError).response?.status === 404) {
        return false;
      }
      return failureCount < 3;
    },
  });

  // If no programId, try to get default program (for backward compatibility)
  const {
    data: defaultProgram,
    isLoading: isLoadingDefault,
    error: queryError,
  } = useQuery({
    queryKey: ["program"],
    queryFn: () => programService.getProgram(),
    enabled: !programId,
    retry: (failureCount, error) => {
      // Don't retry on 404 (no program exists)
      if ((error as AxiosError).response?.status === 404) {
        return false;
      }
      return failureCount < 3;
    },
  });

  const currentProgram = programId ? program : defaultProgram;
  const isLoading = programId ? isLoadingPrograms : isLoadingDefault;
  const isFetching = programId ? isFetchingPrograms : false;

  // Create/Update program mutation
  const { mutate: saveProgram, isPending: isSaving } = useMutation({
    mutationFn: (settings: ProgramSettingsType & { programId?: string }) => {
      if (currentProgram) {
        return programService.updateProgram(settings as ProgramSettingsType & { programId: string });
      } else {
        return programService.createProgram(settings);
      }
    },
    onSuccess: async () => {
      // Invalidate and refetch program data
      await queryClient.invalidateQueries({ queryKey: ["program"] });
      if (programId) {
        await queryClient.invalidateQueries({ queryKey: ["program", programId] });
      }
      await queryClient.invalidateQueries({ queryKey: ["my-programs"] });
      await queryClient.invalidateQueries({ queryKey: ["program-affiliate-count"] });
      if (!brandDetails) {
        await fetchBrandDetails();
      }
      toast.success(
        currentProgram
          ? "Program settings updated successfully"
          : "Program created successfully"
      );
    },
    onError: (err: unknown) => {
      const errorMessage =
        (err as AxiosError<{ message: string }>)?.response?.data?.message ||
        "Failed to save program settings";
      toast.error(errorMessage);
    },
  });

  // Initialize settings from program data
  const [settings, setSettings] = useState<
    ProgramSettingsType & {
      commissionValueInput?: string;
      discountValueInput?: string;
    }
  >({
    commissionType: (currentProgram?.commissionType as "PERCENTAGE" | "FIXED") || "PERCENTAGE",
    commissionValue: currentProgram ? Number(currentProgram.commissionValue) : 0,
    discountValue: currentProgram ? Number(currentProgram.discountValue) : 0,
    minimumPayoutAmount: 0,
    automaticApproval: currentProgram?.automaticApproval ?? false,
    attributionMethod: (currentProgram?.attributionMethod as "STATIC_CODE" | "SNEAKYLINK") || "STATIC_CODE",
  });

  const [initialSettings, setInitialSettings] = useState<ProgramSettingsType>({
    commissionType: (currentProgram?.commissionType as "PERCENTAGE" | "FIXED") || "PERCENTAGE",
    commissionValue: currentProgram ? Number(currentProgram.commissionValue) : 0,
    discountValue: currentProgram ? Number(currentProgram.discountValue) : 0,
    minimumPayoutAmount: 0,
    automaticApproval: currentProgram?.automaticApproval ?? false,
    attributionMethod: (currentProgram?.attributionMethod as "STATIC_CODE" | "SNEAKYLINK") || "STATIC_CODE",
  });

  const [welcomeModalOpen, setWelcomeModalOpen] = useState(false);

  // Set initial settings when program data is loaded
  useEffect(() => {
    if (currentProgram) {
      const programSettings = {
        commissionType: currentProgram.commissionType || "PERCENTAGE",
        commissionValue: Number(currentProgram.commissionValue),
        discountValue: Number(currentProgram.discountValue),
        minimumPayoutAmount: Number(currentProgram.minimumPayoutAmount) || 0,
        automaticApproval: currentProgram.automaticApproval ?? false,
        attributionMethod: (currentProgram.attributionMethod as "STATIC_CODE" | "SNEAKYLINK") || "STATIC_CODE",
        commissionValueInput: currentProgram.commissionValue.toString(),
        discountValueInput: currentProgram.discountValue.toString(),
        name: currentProgram.name || "",
      };
      setSettings(programSettings);
      setInitialSettings(programSettings);
    } else {
      // For new programs, start with empty inputs
      setSettings({
        commissionType: "PERCENTAGE",
        commissionValue: 0,
        discountValue: 0,
        minimumPayoutAmount: 0,
        automaticApproval: false,
        attributionMethod: "STATIC_CODE",
        commissionValueInput: "",
        discountValueInput: "",
        name: "",
      });
    }
  }, [currentProgram, brandDetails?.program]);

  // Check if program settings have changed
  const programSettingsChanged = () => {
    // For new programs, check if any required fields are filled
    if (!currentProgram) {
      return (
        settings.commissionValue > 0 &&
        settings.discountValue >= 0 &&
        settings.minimumPayoutAmount >= 0
      );
    }

    return (
      settings.commissionValue !== initialSettings.commissionValue ||
      settings.discountValue !== initialSettings.discountValue ||
      settings.minimumPayoutAmount !== initialSettings.minimumPayoutAmount ||
      settings.automaticApproval !== initialSettings.automaticApproval ||
      settings.attributionMethod !== initialSettings.attributionMethod ||
      (settings.name ?? "") !== (initialSettings.name ?? "")
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentProgram?.id) {
      toast.error("Program ID is required");
      return;
    }
    // Ensure commissionType is always PERCENTAGE; attributionMethod is read-only
    const settingsToSave = { ...settings };
    delete settingsToSave.attributionMethod;
    // The name field isn't rendered without the naming feature; sending the
    // prefilled value would trip the backend's plan check for old plans.
    if (!hasProgramNamingFeature) {
      delete settingsToSave.name;
    }
    saveProgram({
      ...settingsToSave,
      commissionType: "PERCENTAGE",
      programId: currentProgram.id,
    });
  };

  const error = programId 
    ? programError
      ? (programError as AxiosError<{ message: string }>)?.response?.data?.message || 
        ((programError as AxiosError)?.response?.status === 404 ? "Program not found" : "Failed to load program settings")
      : null
    : queryError
      ? (queryError as AxiosError<{ message: string }>)?.response?.data?.message || "Failed to load program settings"
      : null;

  const commissionDisplay =
    settings.commissionValueInput ??
    String(currentProgram?.commissionValue ?? settings.commissionValue ?? "");
  const discountDisplay =
    settings.discountValueInput ??
    String(currentProgram?.discountValue ?? settings.discountValue ?? "");
  const attributionLabel =
    (currentProgram?.attributionMethod ?? settings.attributionMethod) === "SNEAKYLINK"
      ? "Sneakylink"
      : "Static Code";

  return (
    <div className="flex min-h-[calc(100dvh-3rem)] w-full flex-col px-4 pt-2 pb-4 md:min-h-[calc(100dvh-4rem)]">
      <div className="mx-auto flex w-full max-w-6xl flex-1 flex-col gap-3">
        <div className="flex shrink-0 items-start justify-between gap-3">
          <div>
            <h1 className="text-lg font-semibold text-foreground">
              {currentProgram?.isDefault
                ? "Discoverable Program Settings"
                : "Program Settings"}
            </h1>
            <p className="text-sm text-muted-foreground">
              Configure your affiliate program and manage who's assigned.
            </p>
          </div>
          <button
            type="button"
            onClick={() => navigate("/brand/programs")}
            className="shrink-0 rounded-md p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground"
            aria-label="Close and go back to programs"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {!isLoading && !isFetching && !currentProgram && (
          <Alert variant="warning">
            <AlertDescription>
              No program settings found. Create your program settings below to
              start accepting affiliates.
            </AlertDescription>
          </Alert>
        )}
        {error && (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}
        {!isLoading && currentProgram && currentProgram.status === 'SUSPENDED' && (
          <Alert variant="warning">
            <AlertDescription>
              <h4 className="font-semibold mb-1">Program Suspended</h4>
              <p className="text-sm">
                This program uses Sneakylink attribution, which is not available in your current plan.
                The program has been suspended. Please upgrade to a Scale plan to reactivate this program.
              </p>
              <a
                href="/brand/settings?tab=pricing"
                className="mt-2 inline-block text-sm font-medium underline"
              >
                Upgrade your plan →
              </a>
            </AlertDescription>
          </Alert>
        )}

        {isLoading || isFetching ? (
          <ProgramEditSkeleton />
        ) : (
          <form onSubmit={handleSubmit} className="shrink-0">
            <section className="rounded-xl border border-border bg-card shadow-sm">
              <button
                type="button"
                onClick={() => setSettingsOpen((open) => !open)}
                className="flex w-full items-center gap-3 px-5 py-3.5 text-left"
                aria-expanded={settingsOpen}
              >
                <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-brand-100 text-brand-700">
                  <Settings className="w-4 h-4" />
                </span>
                <span className="flex-1 min-w-0">
                  <span className="block text-sm font-semibold text-foreground">
                    Program configuration
                  </span>
                  <span className="block text-xs text-muted-foreground">
                    Commission, discount, attribution &amp; storefront
                  </span>
                </span>
                {!settingsOpen && (
                  <span className="hidden sm:flex items-center gap-2 mr-2">
                    <span className="rounded-full bg-muted px-2.5 py-1 text-[11px] font-medium text-muted-foreground">
                      {commissionDisplay || "0"}% commission
                    </span>
                    <span className="rounded-full bg-muted px-2.5 py-1 text-[11px] font-medium text-muted-foreground">
                      {discountDisplay || "0"}% off
                    </span>
                    <span className="rounded-full bg-brand-50 px-2.5 py-1 text-[11px] font-medium text-brand-700">
                      {attributionLabel}
                    </span>
                  </span>
                )}
                <ChevronDown
                  className={`w-5 h-5 text-muted-foreground transition-transform ${
                    settingsOpen ? "rotate-180" : ""
                  }`}
                />
              </button>

              {settingsOpen && (
                <div className="border-t border-border px-5 py-5 space-y-5">
                  {!currentProgram?.isDefault && currentProgram && (
                    <div className="flex gap-3 rounded-lg bg-brand-50 p-3 text-sm ring-1 ring-brand-100">
                      <Info className="w-5 h-5 shrink-0 text-brand-600" />
                      <p className="text-muted-foreground">
                        <span className="font-medium text-foreground">Who can join:</span>{" "}
                        this program isn't listed publicly — only your{" "}
                        <span className="font-medium">discoverable program</span> is.
                        Affiliates join this one through its share link (copy it from{" "}
                        <span className="font-medium">Join Portal Link</span>), or you
                        can assign them manually below.
                      </p>
                    </div>
                  )}

                  <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-x-5 gap-y-4">
                    {/* Commission Rate */}
                    <div id="PSF-commission-rate-field">
                      <label className="block text-xs font-medium text-muted-foreground mb-1">
                        Commission rate
                      </label>
                      <div className="relative">
                        <input
                          type="number"
                          step="1"
                          value={commissionDisplay}
                          onChange={(e) => {
                            const input = e.target.value;
                            setSettings({
                              ...settings,
                              commissionValueInput: input,
                              commissionValue: input === "" ? 0 : Number(input),
                            });
                          }}
                          min="0"
                          max="100"
                          className="w-full rounded-lg border border-border px-3 py-2 text-sm pr-8 focus:border-brand-500 focus:ring-2 focus:ring-brand-100 outline-none"
                        />
                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">
                          %
                        </span>
                      </div>
                    </div>

                    {/* Discount Percentage */}
                    <div id="PSF-discount-percentage-field">
                      <label className="block text-xs font-medium text-muted-foreground mb-1">
                        Discount percentage
                      </label>
                      <div className="relative">
                        <input
                          type="number"
                          value={discountDisplay}
                          onChange={(e) => {
                            const input = e.target.value;
                            setSettings({
                              ...settings,
                              discountValueInput: input,
                              discountValue: input === "" ? 0 : Number(input),
                            });
                          }}
                          min="0"
                          max="100"
                          className="w-full rounded-lg border border-border px-3 py-2 text-sm pr-8 focus:border-brand-500 focus:ring-2 focus:ring-brand-100 outline-none"
                        />
                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">
                          %
                        </span>
                      </div>
                    </div>

                    {/* Attribution Method - Read-only display */}
                    {currentProgram && (
                      <div id="PSF-attribution-method-field">
                        <label className="block text-xs font-medium text-muted-foreground mb-1">
                          Attribution method
                        </label>
                        <div className="flex items-center gap-2 rounded-lg border border-border bg-muted px-3 py-2 text-sm text-muted-foreground">
                          <span className="h-2 w-2 rounded-full bg-brand-500" />
                          {attributionLabel}
                          <span className="ml-auto text-[11px] text-muted-foreground">
                            locked
                          </span>
                        </div>
                      </div>
                    )}

                    {/* Program Name - Only for Growth and Scale plans */}
                    {hasProgramNamingFeature && (
                      <div
                        id="PSF-program-name-field"
                        className="sm:col-span-2 xl:col-span-2"
                      >
                        <label className="block text-xs font-medium text-muted-foreground mb-1">
                          Program name{" "}
                          <span className="text-muted-foreground font-normal">
                            (optional)
                          </span>
                        </label>
                        <input
                          type="text"
                          value={settings.name ?? ""}
                          onChange={(e) =>
                            setSettings({
                              ...settings,
                              name: e.target.value,
                            })
                          }
                          placeholder="e.g., Summer Campaign, VIP Program"
                          maxLength={100}
                          className="w-full rounded-lg border border-border px-3 py-2 text-sm focus:border-brand-500 focus:ring-2 focus:ring-brand-100 outline-none"
                        />
                      </div>
                    )}

                    {/* Storefront Welcome Popup */}
                    {currentProgram && (
                      <div
                        id="PSF-welcome-popup-field"
                        className="xl:col-span-1 sm:col-span-2"
                      >
                        <label className="block text-xs font-medium text-muted-foreground mb-1">
                          Storefront welcome popup
                        </label>
                        <div className="flex items-center justify-between rounded-lg border border-brand-100 bg-brand-50/60 px-3 py-2">
                          <p className="text-xs text-muted-foreground truncate pr-2">
                            &ldquo;Sarah sent you{" "}
                            {currentProgram.discountValue
                              ? `${currentProgram.discountValue}% off`
                              : "a discount"}
                            &rdquo;
                          </p>
                          <button
                            type="button"
                            onClick={() => setWelcomeModalOpen(true)}
                            className="text-xs font-medium text-brand-700 hover:underline shrink-0"
                          >
                            See how →
                          </button>
                        </div>
                      </div>
                    )}

                    {/* Automatic approval + share link share one row.
                        A flex row rather than grid cells: the share link needs
                        room for a full URL, so it claims whatever is left and
                        drops to its own line once that falls below its min
                        width — something fixed grid columns can't express. */}
                    <div className="sm:col-span-2 xl:col-span-3 flex flex-wrap items-start gap-x-5 gap-y-4">
                      {/* Automatic Approval Toggle - every program has one.
                          Custom programs are joined through their share link,
                          so auto-approval applies to them too. */}
                      <div
                        id="PSF-automatic-approval-field"
                        className="w-full sm:w-56 shrink-0"
                      >
                        <label className="block text-xs font-medium text-muted-foreground mb-1">
                          Automatic approval
                        </label>
                        <div className="flex items-center justify-between rounded-lg border border-border px-3 py-2">
                          <p className="text-xs text-muted-foreground">
                            {currentProgram && !currentProgram.isDefault
                              ? "Auto-approve share-link joins"
                              : "Auto-approve new applications"}
                          </p>
                          <button
                            type="button"
                            onClick={() =>
                              setSettings({
                                ...settings,
                                automaticApproval: !settings.automaticApproval,
                              })
                            }
                            className={`${
                              settings.automaticApproval
                                ? "bg-brand-600"
                                : "bg-muted"
                            } relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-2`}
                          >
                            <span
                              className={`${
                                settings.automaticApproval
                                  ? "translate-x-5"
                                  : "translate-x-0"
                              } pointer-events-none inline-block h-5 w-5 transform rounded-full bg-card shadow ring-0 transition duration-200 ease-in-out`}
                            />
                          </button>
                        </div>
                      </div>

                      {/* Share link — how affiliates reach this specific program */}
                      {currentProgram && (
                        <ShareLinkField
                          programId={currentProgram.id}
                          shareToken={currentProgram.shareToken}
                          programLabel={
                            currentProgram.isDefault &&
                            (!currentProgram.name ||
                              currentProgram.name === "Default Program")
                              ? "Discoverable Program"
                              : currentProgram.name || "Custom Program"
                          }
                          className="min-w-0 flex-1 basis-[26rem]"
                        />
                      )}
                    </div>
                  </div>

                  {/* Program Settings Action Buttons - Only show when changes are made */}
                  {programSettingsChanged() && (
                    <div className="flex justify-end gap-3 border-t border-border pt-4">
                      <button
                        type="button"
                        onClick={() => {
                          setSettings({ ...initialSettings });
                        }}
                        className="rounded-lg px-4 py-2 text-sm font-medium text-muted-foreground hover:bg-muted"
                      >
                        Discard
                      </button>
                      <Button
                        id="PSF-save-program-button"
                        type="submit"
                        disabled={isSaving}
                      >
                        {isSaving && (
                          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        )}
                        <span>
                          {currentProgram ? "Save changes" : "Create program"}
                        </span>
                      </Button>
                    </div>
                  )}
                </div>
              )}
            </section>
          </form>
        )}

        {/* Brand tiers — commission steps affiliates climb by sales volume */}
        {!isLoading && currentProgram && (
          <ProgramTiersCard
            programId={currentProgram.id}
            baseCommissionValue={Number(currentProgram.commissionValue)}
            commissionType={currentProgram.commissionType}
          />
        )}

        {/* Renewal commissions — only for brands with Recharge connected */}
        {!isLoading && currentProgram && (
          <RenewalCommissionCard
            programId={currentProgram.id}
            baseCommissionValue={Number(currentProgram.commissionValue)}
            settings={{
              renewalsEnabled: currentProgram.renewalsEnabled ?? false,
              renewalRateType: currentProgram.renewalRateType ?? "PERCENTAGE",
              renewalRate: Number(currentProgram.renewalRate ?? 0),
              renewalPaidCap: currentProgram.renewalPaidCap ?? null,
            }}
          />
        )}

        {/* Join page content — inherits the brand default until customized */}
        {!isLoading && currentProgram && (
          <JoinContentCard programId={currentProgram.id} />
        )}

        {/* Affiliate Management — assigned / available / applicants.
            Every program gets the same three tabs, including the discoverable
            one: now that any program can be joined through its share link, the
            old "custom programs are assign-only" split no longer holds. */}
        {!isLoading && currentProgram && (
          <AffiliateManagementCard
            programId={currentProgram.id}
            isSuspended={currentProgram.status === "SUSPENDED"}
            automaticApproval={currentProgram.automaticApproval}
            attributionMethod={currentProgram.attributionMethod}
            className="flex-1"
          />
        )}

        <DialogRoot
          open={welcomeModalOpen}
          onOpenChange={setWelcomeModalOpen}
          className="max-w-2xl"
        >
          <DialogContent className="max-h-[90vh] overflow-y-auto">
            <DialogHeader
              showCloseButton
              onClose={() => setWelcomeModalOpen(false)}
            >
              Custom welcome modal for your storefront
            </DialogHeader>

            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Offer visitors a branded welcome experience and surface your affiliate
                discount from your Shopify theme. Configure the modal in your Shopify
                admin so it matches your store.
              </p>

              <div>
                <img
                  src={WELCOME_MODAL_ASSETS.screenshot}
                  alt="Example of the Reveshare welcome modal on a storefront"
                  className="w-full h-auto rounded-lg border border-border"
                />
              </div>

              <video
                controls
                playsInline
                preload="metadata"
                className="w-full max-h-[50vh] rounded-lg object-contain bg-foreground/5"
              >
                <source
                  src={WELCOME_MODAL_ASSETS.demoVideo}
                  type="video/mp4"
                />
              </video>

              <p className="text-sm text-muted-foreground">
                Follow the walkthrough video above to turn the feature on from your
                Shopify admin dashboard.
              </p>
            </div>

            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setWelcomeModalOpen(false)}
              >
                Got it
              </Button>
            </DialogFooter>
          </DialogContent>
        </DialogRoot>
      </div>
    </div>
  );
}
